"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DateField = void 0;
const react_1 = __importStar(require("react"));
const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];
/** Convert DD/MM/YYYY or YYYY-MM-DD → { day, month, year } strings ('' if missing). */
function parseDate(v) {
    if (!v)
        return { day: '', month: '', year: '' };
    const s = String(v).trim();
    if (s.includes('-') && s.indexOf('-') === 4) {
        const [y, m, d] = s.split('-');
        return { day: d || '', month: m || '', year: y || '' };
    }
    if (s.includes('/')) {
        const [d, m, y] = s.split('/');
        return { day: d || '', month: m || '', year: y || '' };
    }
    return { day: '', month: '', year: '' };
}
/** Parse a date string in DD/MM/YYYY or YYYY-MM-DD → Date | null. */
function toDate(s) {
    if (!s)
        return null;
    if (s.includes('-') && s.indexOf('-') === 4) {
        const [y, m, d] = s.split('-').map((x) => parseInt(x, 10));
        return new Date(y, m - 1, d);
    }
    if (s.includes('/')) {
        const [d, m, y] = s.split('/').map((x) => parseInt(x, 10));
        return new Date(y, m - 1, d);
    }
    return null;
}
function daysInMonth(month, year) {
    if (!month || !year)
        return 31;
    return new Date(year, month, 0).getDate();
}
const DateField = ({ field, value, onChange, error, disabled }) => {
    // Local state keeps partial selections (only day picked, or only day + month)
    // so the UI doesn't lose the user's clicks while they're still filling out
    // the other two dropdowns. Only when all three are set do we emit a value
    // to the parent form.
    const initial = (0, react_1.useMemo)(() => parseDate(value), [value]);
    const [day, setDay] = (0, react_1.useState)(initial.day);
    const [month, setMonth] = (0, react_1.useState)(initial.month);
    const [year, setYear] = (0, react_1.useState)(initial.year);
    // Sync from external value ONLY when it's a complete date (prefill /
    // programmatic reset). If the incoming value is empty or partial, we
    // ignore it — that's usually just the parent's own state catching up
    // after our own onChange('') and would otherwise wipe partial selections.
    (0, react_1.useEffect)(() => {
        const p = parseDate(value);
        if (p.day && p.month && p.year) {
            setDay(p.day);
            setMonth(p.month);
            setYear(p.year);
        }
        else if (!value) {
            // Genuine reset by parent (e.g. form clear) — clear local too.
            if (!day && !month && !year)
                return;
            // If the parent had a value before and now cleared it deliberately,
            // fall through to also clear local; but only when local was itself
            // a full date (matches what parent held). Otherwise this is our own
            // onChange('') echo — keep partial state.
            if (initial.day || initial.month || initial.year) {
                setDay('');
                setMonth('');
                setYear('');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    const minD = toDate(field.validation?.minDate);
    const maxD = toDate(field.validation?.maxDate);
    const yearRange = (0, react_1.useMemo)(() => {
        const now = new Date().getFullYear();
        const maxY = maxD ? maxD.getFullYear() : now;
        const minY = minD ? minD.getFullYear() : maxY - 100;
        const years = [];
        for (let y = maxY; y >= minY; y--)
            years.push(y);
        return years;
    }, [minD, maxD]);
    const numDays = daysInMonth(parseInt(month || '0', 10), parseInt(year || '0', 10));
    const emit = (d, m, y) => {
        if (d && m && y) {
            onChange(field.key, `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`);
        }
        else {
            onChange(field.key, '');
        }
    };
    const handleDay = (v) => {
        setDay(v);
        emit(v, month, year);
    };
    const handleMonth = (v) => {
        setMonth(v);
        // If the newly-picked month has fewer days than the current day, keep the
        // day as-is unless it's out of range — clamp instead of wiping it.
        let d = day;
        const cap = daysInMonth(parseInt(v || '0', 10), parseInt(year || '0', 10));
        if (d && parseInt(d, 10) > cap)
            d = String(cap).padStart(2, '0');
        setDay(d);
        emit(d, v, year);
    };
    const handleYear = (v) => {
        setYear(v);
        let d = day;
        const cap = daysInMonth(parseInt(month || '0', 10), parseInt(v || '0', 10));
        if (d && parseInt(d, 10) > cap)
            d = String(cap).padStart(2, '0');
        setDay(d);
        emit(d, month, v);
    };
    const selectCls = `field-input${error ? ' error' : ''}`;
    return (react_1.default.createElement("div", { className: "field-group" },
        react_1.default.createElement("label", { className: "field-label", htmlFor: `${field.key}-day` },
            field.label,
            field.required && react_1.default.createElement("span", { className: "required" }, "*")),
        react_1.default.createElement("div", { className: "wf-dob" },
            react_1.default.createElement("select", { id: `${field.key}-day`, name: `${field.key}-day`, className: selectCls, value: day, onChange: (e) => handleDay(e.target.value), disabled: disabled, "aria-label": `${field.label} — day` },
                react_1.default.createElement("option", { value: "" }, "Day"),
                Array.from({ length: numDays }, (_, i) => i + 1).map((d) => (react_1.default.createElement("option", { key: d, value: String(d).padStart(2, '0') }, d)))),
            react_1.default.createElement("select", { name: `${field.key}-month`, className: selectCls, value: month, onChange: (e) => handleMonth(e.target.value), disabled: disabled, "aria-label": `${field.label} — month` },
                react_1.default.createElement("option", { value: "" }, "Month"),
                MONTHS.map((label, i) => (react_1.default.createElement("option", { key: label, value: String(i + 1).padStart(2, '0') }, label)))),
            react_1.default.createElement("select", { name: `${field.key}-year`, className: selectCls, value: year, onChange: (e) => handleYear(e.target.value), disabled: disabled, "aria-label": `${field.label} — year` },
                react_1.default.createElement("option", { value: "" }, "Year"),
                yearRange.map((y) => (react_1.default.createElement("option", { key: y, value: String(y) }, y))))),
        field.tooltip && react_1.default.createElement("div", { className: "field-hint" }, field.tooltip),
        error && react_1.default.createElement("div", { className: "field-error" }, error)));
};
exports.DateField = DateField;
